import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useFormattedDuration, AttributesContent } from 'modules/notes/common';
import { TimeSignal, TimeSignalAttribute } from 'graphql/types';
import { ListLabels } from 'modules/notes/list/ListLabels';
import { NoteClassificationTag } from 'modules/notes/list/NoteClassificationTag';
import { NoteSourceGroup } from 'modules/notes/list/NoteSourceGroup';
import { useLabelInfo } from 'modules/notes/Timeline/hooks';
import { useSplitEntriesContext } from '../SplitEntriesProvider';
import { OFFSET_ENTRY } from '../types';
import { CustomLabelsList } from '../../../customLabels/components/CustomLabelsList';
import { useFeatureFlag } from '../../../common/hooks';
import { ExcludedTag } from '../../list/ExcludedTag';
import { NoteTimeDuration } from './NoteTimeDuration';

type StyleProps = {
  hasSource: boolean;
};

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(3),
    backgroundColor: theme.palette.table?.header
  },
  contentRoot: {
    display: 'flex',
    margin: theme.spacing(0, 2),
    marginLeft: ({ hasSource }: StyleProps) => theme.spacing(hasSource ? 0 : 2)
  },
  content: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column'
  },
  firstLine: {
    alignSelf: 'center',
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between'
  },
  secondLine: {
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'space-between'
  },
  secondLineLabels: {
    display: 'flex',
    flexDirection: 'column'
  },
  checkboxAndSource: {
    display: 'flex',
    minWidth: ({ hasSource }: StyleProps) =>
      hasSource ? theme.spacing(6) : theme.spacing(0.5),
    alignItems: 'center',
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1)
  },
  description: {
    marginRight: theme.spacing(1),
    ...theme.typography.body2,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden'
  },
  emptyNote: {
    fontStyle: 'italic',
    color: theme.palette.text.secondary
  },
  timeContainer: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  time: {
    margin: theme.spacing(0, 0.75, 0.75, 0.75)
  },
  duration: {
    minWidth: theme.spacing(7),
    margin: theme.spacing(0, 2, 0, 0),
    textAlign: 'right',
    marginRight: 0
  },
  tag: {
    minWidth: 'unset',
    textAlign: 'right',
    margin: theme.spacing(0, 0, 0, 2)
  },
  listLabels: {
    flexWrap: 'wrap'
  },
  moreBtn: {
    marginLeft: `${theme.spacing(2)} !important`
  },
  tagContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'flex-end',
    alignItems: 'flex-start'
  }
}));

const useCustomlabelSyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2.3),
    flexWrap: 'wrap',
    marginLeft: theme.spacing(-0.7)
  }
}));
const useCustomlabelchipSyles = makeStyles((theme) => ({
  root: {
    paddingBottom: theme.spacing(0.4)
  }
}));

type NoteDescriptionContentProps = {
  note: TimeSignal;
  attributes?: TimeSignalAttribute[];
};

export const NoteDescriptionContent: React.FC<NoteDescriptionContentProps> = ({
  note,
  note: { sources, startTime, endTime, duration },
  attributes
}: NoteDescriptionContentProps) => {
  const isTIWebGroupViewEnabled: boolean = useFeatureFlag(
    'isTIWebGroupViewEnabled',
    false
  );

  const classes = useStyles({
    hasSource: Boolean(sources.length)
  });
  const chipClasses = useCustomlabelSyles();
  const customLabelList = useCustomlabelchipSyles();

  const { timesheetLabels, customLabels } = useLabelInfo({ note });

  const listClasses = useMemo(() => ({ root: classes.listLabels }), [
    classes.listLabels
  ]);

  const timeClasses = useMemo(
    () => ({
      root: classes.timeContainer,
      time: classes.time,
      duration: classes.duration
    }),
    [classes.duration, classes.time, classes.timeContainer]
  );

  return (
    <div className={classes.root}>
      <OffsetDuration originalNoteDuration={duration ?? 0} />
      <div data-testid="note-description-body" className={classes.contentRoot}>
        {sources && Boolean(sources.length) && (
          <div className={classes.checkboxAndSource}>
            <NoteSourceGroup sources={sources} />
          </div>
        )}
        <div className={classes.content}>
          <div className={classes.firstLine}>
            <Typography
              data-testid="note-description"
              variant="body2"
              className={classnames(
                classes.description,
                note.description ? null : classes.emptyNote
              )}
            >
              {note.description || <FormattedMessage id="note.emptyNote" />}
            </Typography>
            <NoteTimeDuration
              classes={timeClasses}
              startTime={startTime}
              endTime={endTime}
              duration={note.duration}
            />
          </div>
          <div className={classes.secondLine}>
            <div className={classes.secondLineLabels}>
              <ListLabels
                labels={timesheetLabels}
                multiline
                classes={listClasses}
                areChipsClickable={false}
              />
              <CustomLabelsList
                labels={customLabels}
                classes={chipClasses}
                chipClasses={customLabelList}
                multiline={true}
              />
            </div>
            <div className={classes.tagContainer}>
              {isTIWebGroupViewEnabled && note.excluded && (
                <ExcludedTag note={note}></ExcludedTag>
              )}
              <NoteClassificationTag
                className={classes.tag}
                noteClassification={note.classification}
              />
            </div>
          </div>
        </div>
      </div>
      <AttributesContent
        attributes={attributes}
        moreClassName={classes.moreBtn}
      />
    </div>
  );
};

type OffsetDurationProps = {
  originalNoteDuration: number;
};
const useOffsetStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    top: theme.spacing(0),
    right: theme.spacing(2),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0, 0.75)
  }
}));
const OffsetDuration: React.FC<OffsetDurationProps> = ({
  originalNoteDuration
}: OffsetDurationProps) => {
  const classes = useOffsetStyles();
  const { splitEntries } = useSplitEntriesContext();
  const offsetDuration = useMemo(() => {
    const actualHours = splitEntries.reduce((acc, split) => {
      if (split.id === OFFSET_ENTRY) {
        return acc;
      }
      return acc + (split.duration ?? 0);
    }, 0);
    return actualHours - originalNoteDuration;
  }, [originalNoteDuration, splitEntries]);
  const formattedOffsetDuration = useFormattedDuration({
    needsMask: true,
    duration: Math.abs(offsetDuration ?? 0)
  });

  if (!offsetDuration) {
    return <></>;
  }
  return (
    <Typography
      className={classes.root}
      variant="caption"
      data-testid="split-offset-duration"
    >
      {offsetDuration > 0 ? '+' : '-'}
      {formattedOffsetDuration}
    </Typography>
  );
};
